import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { articlesFor, categoriesFor, toCard, pathsFor, viewsNum } from "./reading";
import { ArticleCard, SectionHeader } from "./components";

const AUDIENCES = ["All", "MLS admin", "Broker", "Agent", "Developer"];

const ASK_SUGGESTIONS: Record<string, string[]> = {
  recursive: ["Why did my listing fail validation on lot size?", "How do I revoke a scraper feed?", "What does RESO certification check?", "Who can change a board rule?"],
  solidearth: ["How often does a saved search run?", "Mapping identity groups to roles", "Can a broker syndicate off-market?", "Publishing a page without a release"],
  nautilent: ["How is a cap prorated mid-year?", "What clears a compliance checklist item?", "Webhook retries and signatures", "Roster sync with the MLS"],
};

export function PortalHome() {
  const { brand, brandId, spaceId, user } = usePortal();
  const navigate = useNavigate();
  const [aud, setAud] = useState("All");
  const [q, setQ] = useState("");

  const cats = categoriesFor(brandId, spaceId, aud);
  const arts = articlesFor(brandId, spaceId);
  const popular = [...arts].sort((a, b) => viewsNum(b.views) - viewsNum(a.views)).slice(0, 4).map((a) => toCard(a, brand, "mark", user.org));
  const paths = pathsFor(brandId);
  const inProgress = paths.filter((p) => p.done > 0 && p.done < p.steps).slice(0, 2);

  function ask(query: string) {
    const t = query.trim();
    navigate({ to: "/portal/ask", search: t ? { q: t } : {} });
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "34px 28px 64px", display: "flex", flexDirection: "column", gap: 34 }}>
      {/* Ask hero */}
      <section>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: "-1px", color: "#1d241f" }}>How can we help, {user.name.split(" ")[0]}?</h1>
        <p style={{ margin: "8px 0 0", fontSize: 14.5, lineHeight: 1.6, color: "#8a8e88" }}>Ask a question in your own words, or browse the documentation for {brand.name}.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask(q);
          }}
          style={{ display: "flex", gap: 10, marginTop: 18 }}
        >
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.1)", padding: "12px 14px" }}>
            <Icon name="search" size={18} color={brand.ink} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a question or search" style={{ flex: 1, border: 0, outline: 0, fontSize: 14.5, color: "#1d241f", fontFamily: "inherit" }} />
          </div>
          <button type="submit" style={{ border: 0, background: brand.ink, color: "#fff", borderRadius: 3, padding: "0 22px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", cursor: "pointer" }}>Ask</button>
        </form>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {(ASK_SUGGESTIONS[brandId] ?? []).map((s) => (
            <button key={s} onClick={() => ask(s)} style={{ border: `1px solid ${brand.line}`, background: brand.soft, color: brand.ink, borderRadius: 999, padding: "6px 12px", fontSize: 12.5, cursor: "pointer" }}>{s}</button>
          ))}
        </div>
      </section>

      {/* Browse the documentation */}
      <section>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <SectionHeader>Browse the documentation</SectionHeader>
          <span style={{ flex: 1 }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {AUDIENCES.map((a) => {
              const on = aud === a;
              return (
                <button key={a} onClick={() => setAud(a)} style={{ border: `1px solid ${on ? brand.line : "#dcdad2"}`, background: on ? brand.soft : "#fff", color: on ? brand.ink : "#4a504a", borderRadius: 999, padding: "5px 11px", fontSize: 12, fontWeight: on ? 600 : 500, cursor: "pointer" }}>{a}</button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap: 14 }}>
          {cats.map((c) => (
            <Link key={c.name} to="/portal/guides" search={{ cat: c.name }} style={{ textDecoration: "none", color: "inherit", background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 34, height: 34, borderRadius: 3, background: brand.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={c.icon} size={17} color={brand.ink} />
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: "#1d241f" }}>{c.name}</span>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#8a8e88", textWrap: "pretty" }}>{c.blurb}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, fontSize: 11.5, color: "#8a8e88" }}>
                <span>{c.audience}</span>
                <span>·</span>
                <span style={{ fontFamily: "'Space Mono', monospace" }}>{c.count}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Continue onboarding */}
      {inProgress.length > 0 && (
        <section>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
            <SectionHeader>Continue your onboarding</SectionHeader>
            <span style={{ flex: 1 }} />
            <Link to="/portal/onboarding" style={{ fontSize: 12.5, color: brand.ink, textDecoration: "none" }}>All paths →</Link>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 14 }}>
            {inProgress.map((p) => {
              const pct = Math.round((p.done / p.steps) * 100);
              return (
                <Link key={p.name} to="/portal/onboarding" style={{ textDecoration: "none", color: "inherit", background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 16px" }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "#1d241f" }}>{p.name}</div>
                  <div style={{ fontSize: 12.5, color: "#8a8e88", marginTop: 4, textWrap: "pretty" }}>{p.blurb}</div>
                  <div style={{ height: 4, borderRadius: 2, background: "#f0efe9", marginTop: 12 }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 2, background: brand.ink }} />
                  </div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 7 }}>{p.done}/{p.steps} · {p.mins} min</div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Most read */}
      <section>
        <div style={{ marginBottom: 14 }}><SectionHeader>Most read this month</SectionHeader></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 14 }}>
          {popular.map((c) => (
            <ArticleCard key={c.id} card={c} brand={brand} />
          ))}
        </div>
      </section>
    </div>
  );
}
